import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import BugReportIcon from '@mui/icons-material/BugReport';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GridViewIcon from '@mui/icons-material/GridView';
import DoneIcon from '@mui/icons-material/Done';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { getTaskById, updateTask } from '../../../services/taskService';
import capitalizeWords from '../../../utils/capitalize';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
import getStyles from '../../../utils/getStylesForTaskStatusSelect';
import formatDate from '../../../utils/formatDate';

export default function Component() {
  const [status, setStatus] = useState('In Progress');
  const { id } = useParams();
  const [task, setTask] = useState({
    taskName: '',
    user: {
      userName: '',
    },
    projects: {
      projectName: '',
    },
    status: '',
    taskType: '',
    description: '',
    startDate: null,
    endDate: null,
    plannedHours: 0,
    section: {
      name: '',
    },
    issueType: '',
    parentTask: {
      taskName: '',
    },
    creator: {
      userName: '',
    },
    completedAt: null,
  });

  const getTaskDetail = async () => {
    try {
      const response = await getTaskById(id);
      if (response?.data?.data) {
        setTask(response.data.data);
      } else {
        toast.error(response?.data?.message ?? response.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    getTaskDetail();
  }, [id]);

  const handleStatusChange = async (event) => {
    const newStatus = event.target.value;
    try {
      await updateTask(id, { status: newStatus });
      await getTaskDetail(); // Re-fetch the task data
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error(error?.response?.data?.message ?? error.message);
    }
  };

  const stripHtmlTags = (str) => {
    if (!str) return '';
    return str.replace(/<\/?[^>]+(>|$)/g, '');
  };

  const breadcrumbs = [
    {
      label: 'Home',
      path: '/',
    },
    {
      label: 'My Task',
      path: '/mytask',
    },
    {
      label: task.taskName,
      path: '',
    },
  ];

  return (
    <Card sx={{ maxWidth: 1000, margin: 'auto', padding: 3 }}>
      <CardHeader
        title={<BreadCrumb breadcrumbs={breadcrumbs} />}
        subheader={
          <Box display="flex" alignItems="center" gap={2}>
            <AssignmentIndIcon sx={{ fontSize: 28 }} />
            <Typography variant="body1">{task.user?.userName ? capitalizeWords(task.user.userName) : 'N/A'}</Typography>
          </Box>
        }
        action={
          <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={task.status}
              renderValue={(selected) => (
                <Box sx={{ ...getStyles(selected), padding: '2px 8px', borderRadius: '4px' }}>{selected}</Box>
              )}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      '&.NotStarted': { color: '#161C24', backgroundColor: '#DFE3E8' },
                      '&.InProgress': { color: '#d49116', backgroundColor: '#fff3bb' },
                      '&.Completed': { color: '#188d1a', backgroundColor: '#b3ffb3' },
                      '&.Blocked': { color: 'red', backgroundColor: 'lightpink' },
                      '&.Reopened': { color: '#0C53B7', backgroundColor: '#D0F2FF' },
                    },
                  },
                },
              }}
              onChange={handleStatusChange}
            >
              <MenuItem value="Not Started" className="NotStarted">
                Not Started
              </MenuItem>
              <MenuItem value="In Progress" className="InProgress">
                In Progress
              </MenuItem>
              <MenuItem value="Completed" className="Completed">
                Completed
              </MenuItem>
              <MenuItem value="Blocked" className="Blocked">
                Blocked
              </MenuItem>
              <MenuItem value="Reopened" className="Reopened">
                Reopened
              </MenuItem>
            </Select>
          </FormControl>
        }
      />
      <CardContent sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
        <Box>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <WorkIcon sx={{ fontSize: 28 }} />
            <Typography variant="body1">
              {task.projects?.projectName ? capitalizeWords(task.projects.projectName) : 'N/A'}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <GridViewIcon sx={{ fontSize: 28 }} />
            <Typography variant="body1">{task?.section?.name ? capitalizeWords(task.section.name) : 'NA'}</Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <CalendarTodayIcon sx={{ fontSize: 28 }} />
            <Typography variant="body1">
              {formatDate(task.startDate)} - {formatDate(task.endDate)}
            </Typography>
          </Box>

          {task.taskType?.toLowerCase() === 'issue' && (
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <BugReportIcon sx={{ fontSize: 28 }} />
              <Typography variant="body1">{task?.issueType ? capitalizeWords(task.issueType) : 'N/A'}</Typography>
            </Box>
          )}
        </Box>
        <Box>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <AssignmentTurnedInIcon sx={{ fontSize: 28 }} />
            <Typography variant="body1">{task?.taskType ? capitalizeWords(task.taskType) : 'N/A'}</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <AccessTimeIcon sx={{ fontSize: 28 }} />
            <Typography variant="body1">{task.plannedHours} hrs</Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <PersonIcon sx={{ fontSize: 28 }} />
            <Typography variant="body1">
              {task?.creator?.userName ? capitalizeWords(task.creator.userName) : 'N/A'}
            </Typography>
          </Box>
          {task.taskType?.toLowerCase() === 'issue' && (
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <BugReportIcon sx={{ fontSize: 28 }} />
              <Typography variant="body1">
                {task?.parentTask?.taskName ? capitalizeWords(task.parentTask.taskName) : 'N/A'}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
      <CardActions>
        <Box display="flex" alignItems="center" gap={2}>
          <DoneIcon sx={{ fontSize: 28 }} />
          <Typography variant="body1">
            Completed on {task?.completedAt ? formatDate(task?.completedAt) : 'N/A'}
          </Typography>
        </Box>
      </CardActions>
      <CardContent>
        <Typography variant="body1">{task.description ? stripHtmlTags(task.description) : 'No description'}</Typography>
      </CardContent>
    </Card>
  );
}
